import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTrackEvent } from '../../../../hooks/useTrackEvent/useTrackEvent';
import { BaseModal } from '../../../../common/components/BaseModal/BaseModal';
import { Drawer, Input, Switch } from 'antd';
import { useStore } from '../../../app/data/root.store';
import { holdingEntityBackgroundColors } from './background-colors/holding-entity-background-colors';
import { ColorSelectionWrapper, HoldingEntityColorsInputTitle, HoldingEntityContentWrapper, DrawerFooter, DrawerHeader, DrawerWrapper, EditHoldingEntityInputRow, FloatingLabeInInput, InputWithLabelOnTopWrapper, MobileCloseCreateHoldingEntityWrapper, MobileHeaderSubTitle, MobileHeaderTitle, ModalCloseButtonWrapper, SelectionContainer, HoldingEntityImageTile, SetAsDefaultInfoWrapper, SetAsDefaultTitle, SetAsDefaultWrapper, SetAsDefaultSubTitle } from './EditHoldingEntity.styles';
import { ReactComponent as ModalCloseButtonSvg } from './svgs/modal-close-button.svg';
import { ReactComponent as MobileBackButtonSvg } from './svgs/mobile-modal-back-button.svg';
import { PrimaryButtonWithIcon, SecondaryButtonWithIcon } from '../../../../common/components/buttons/NewButtons.styles';
import { ActionLockedIconWithTooltip } from '../../../../common/components/ActionLocked/ActionLockedIconWithTooltip';
import { DeleteHoldingEntity } from './DeleteHoldingEntity';
import { successNotification } from '../../../layout/components/Notifications/notifications';

export const EditHoldingEntity = observer((props) => {
  const { uiStore } = useStore();

  useTrackEvent('Popup Viewed', {
    title: 'Holding entity modal opened',
    url: window.location.href
  })

  return (
    uiStore.isDesktopView ?
      ReactDOM.createPortal(<BaseModal width={'512px'} height={'auto'} borderRadius={8}>
        <EditHoldingEntityContent {...props} />
      </BaseModal>, document.body) :
      <Drawer
        closable={false}
        visible={props.drawerVisible !== false}
        width={'100%'}
        // height={'100%'}
        className={'editHoldingEntityDrawerContainer_mobile'}
        placement={'right'}
        destroyOnClose={true}
        push={false}
        onClose={() => { props.onClose() }} >
        <EditHoldingEntityContent {...props} isMobileView />
      </Drawer>
  )
})

export const HOLDING_ENTITY_NAME_MAX_LENGTH = 40;

export const EditHoldingEntityContent = observer(({
  isEdit = false,
  item = {},
  onSave,
  onDelete,
  onClose,
  isMobileView,
}) => {
  const { userStore } = useStore();
  const [holdingEntityData, setHoldingEntityData] = useState({
    name: item.name || '',
    description: item.description || '',
    colorCode: holdingEntityBackgroundColors.find((bgColor) => bgColor === item.colorCode) ? item.colorCode : holdingEntityBackgroundColors[0],
    isDefault: item.isDefault || false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isDelete, setIsDelete] = useState(false);
  const classNameInputRef = useRef(null);

  const isBlockedByRoleOrPaymentStatus = userStore.isReadOnlyMode || userStore.isActionBlockedByRole('addHoldingEntity') || userStore.isActionBlockedByRole('editHoldingEntity');

  useEffect(() => {
    if (classNameInputRef.current) {
      setTimeout(() => {
        classNameInputRef.current.focus();
      }, 100);
    }
  }, []);


  const fieldChange = (field, value) => {
    resetFormInputError(field);
    if (field === 'name' && value?.length > HOLDING_ENTITY_NAME_MAX_LENGTH) {
      // setFormErrors({
      //   ...formErrors,
      //   name: `Name cannot exceed ${HOLDING_ENTITY_NAME_MAX_LENGTH} characters`
      // });
      value = value.substring(0, HOLDING_ENTITY_NAME_MAX_LENGTH);
    }
    setHoldingEntityData({ ...holdingEntityData, [field]: value })
  }

  const updateFormErrors = (data) => {
    const errors = {};
    const fields = [
      { name: 'name', message: 'Name is required' },
    ];

    fields.forEach(field => {
      const value = data[field.name];
      if (!value || !value?.trim()) {
        errors[field.name] = field.message;
      }
    });

    setFormErrors(errors);
    return errors;
  }

  const resetFormInputError = (input) => {
    delete formErrors[input];
  }

  const handleSave = (e) => {
    if (isBlockedByRoleOrPaymentStatus) {
      userStore.setShowLimitedSharedAccess(true);
    } else {
      const trimmedHoldingEntityData = {
        ...holdingEntityData,
        name: holdingEntityData.name?.trim(),
      };

      const errors = updateFormErrors(trimmedHoldingEntityData);
      const isFormValid = Object.keys(errors).length === 0;
      if (isFormValid) {
        onSave?.({
          ...trimmedHoldingEntityData,
          ...(isEdit ? { id: item.id } : {})
        });
        if (isMobileView) {
          onClose(e);
        }
      }
    }
  }

  const mobileViewDeleteHoldingEntity = (e) => {
    if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
    } else {
      setIsDelete(true);
    }
  }

  return (<DrawerWrapper>
    <DrawerHeader>
      {isMobileView ?
        <>
          <div style={{ display: 'flex', flex: 1, maxWidth: 'calc(100% - 40px)' }}>
            {isEdit && <MobileBackButtonSvg onClick={onClose} style={{ minWidth: '40px' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: 'calc(100% - 40px)', ...(!isEdit ? { paddingLeft: '40px' } : {}) }}>
              <HoldingEntityTileComponent
                bgColor={holdingEntityBackgroundColors.find((bgColor) => bgColor === holdingEntityData.colorCode)}
              />
              <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 'calc(100% - 40px)' }}>
                {isEdit ?
                  <>
                    <MobileHeaderTitle>{holdingEntityData.name}</MobileHeaderTitle>
                    <MobileHeaderSubTitle>Edit holding entity</MobileHeaderSubTitle>
                  </> : <MobileHeaderTitle>Create new holding entity</MobileHeaderTitle>}
              </div>
            </div>
            {!isEdit &&
              <>
                <div style={{ flex: 1 }} />
                <MobileCloseCreateHoldingEntityWrapper onClick={onClose}>
                  <MobileCloseCreateHoldingEntity />
                </MobileCloseCreateHoldingEntityWrapper>
              </>
            }
          </div>
        </>
        :
        <>
          {isEdit ? 'Edit' : 'Create new'} holding entity
          <ModalCloseButtonWrapper onClick={onClose}><ModalCloseButtonSvg /></ModalCloseButtonWrapper>
        </>
      }
    </DrawerHeader>

    <HoldingEntityContentWrapper>

      <EditHoldingEntityInputRow>
        <InputWithLabelOnTopWrapper withError={formErrors.name}>
          <FloatingLabeInInput>Name</FloatingLabeInInput>
          <Input style={{ height: '64px' }} ref={classNameInputRef} value={holdingEntityData.name} onChange={(e) => { fieldChange('name', e.target.value) }} />
        </InputWithLabelOnTopWrapper>
      </EditHoldingEntityInputRow>

      <EditHoldingEntityInputRow>
        <InputWithLabelOnTopWrapper withError={formErrors.description}>
          <FloatingLabeInInput>Description</FloatingLabeInInput>
          <Input.TextArea autoSize style={{ height: '64px' }} value={holdingEntityData.description} onChange={(e) => { fieldChange('description', e.target.value) }} />
        </InputWithLabelOnTopWrapper>
      </EditHoldingEntityInputRow>

      <ColorSelectionWrapper>
        <HoldingEntityColorsInputTitle>Color</HoldingEntityColorsInputTitle>
        <SelectionContainer>
          {holdingEntityBackgroundColors.map((bgColor, index) => (
            <HoldingEntityImageTile
              key={index}
              image={bgColor}
              size={isMobileView ? 54 : 44}
              isSelected={holdingEntityData.colorCode === bgColor}
              onClick={() => { fieldChange('colorCode', bgColor) }}
              isSelectable
            />
          ))}
        </SelectionContainer>
      </ColorSelectionWrapper>

      {isMobileView && <SetAsDefaultWrapper onClick={() => { setHoldingEntityData({ ...holdingEntityData, isDefault: !holdingEntityData.isDefault }); successNotification('Holding entity defaults have been successfully set') }}>
        <SetAsDefaultInfoWrapper>
          <SetAsDefaultTitle>Set as default</SetAsDefaultTitle>
          <SetAsDefaultSubTitle>This is the default holding entity when an item is created</SetAsDefaultSubTitle>
        </SetAsDefaultInfoWrapper>
        <div style={{ flex: 1 }} />
        <Switch
          checked={holdingEntityData.isDefault}
          // size="small"
          className='holdingEntityDefaultSwitch' />
      </SetAsDefaultWrapper>}

    </HoldingEntityContentWrapper>

    <DrawerFooter>
      <div style={{ flex: 1 }} />
      {isEdit && isMobileView && <SecondaryButtonWithIcon onClick={mobileViewDeleteHoldingEntity} style={{ color: '#ff0000' }} mobileHeight={56} mobileBorderRadius={12}>
        {userStore.isReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity') ? <ActionLockedIconWithTooltip iconColor={'#ff0000'} marginLeft={'0px'} isRoleBlocked={userStore.isActionBlockedByRole('deleteHoldingEntity')} /> : <EraseHoldingEntitySvg />}
        Delete holding entity</SecondaryButtonWithIcon>}
      <PrimaryButtonWithIcon onClick={handleSave} mobileHeight={56} mobileBorderRadius={12}>
        {isBlockedByRoleOrPaymentStatus && <ActionLockedIconWithTooltip iconColor={'#fff'} marginLeft={'0px'} isRoleBlocked={userStore.isActionBlockedByRole('addHoldingEntity') || userStore.isActionBlockedByRole('editHoldingEntity')} />}
        {isEdit ? 'Save' : 'Create'}
      </PrimaryButtonWithIcon>
    </DrawerFooter>

    {isDelete && <DeleteHoldingEntity item={item} onClose={() => { setIsDelete(false); }} onDelete={() => { onDelete(item.id); }}
      relevantImage={holdingEntityBackgroundColors.find((bgColor) => bgColor === holdingEntityData.colorCode)}
    />}
  </DrawerWrapper>)
})

export const HoldingEntityTileComponent = ({ bgColor }) => {

  return (
    <HoldingEntityImageTile
      image={bgColor}
      size={36}
      borderRadius={6}
    >
      <HoldingEntitySvg />
    </HoldingEntityImageTile>
  )
}

const EraseHoldingEntitySvg = () => {
  return (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.75 9.5C18.75 9.5 18.2262 15.9022 17.9194 18.9881C17.869 19.4947 17.4419 19.8757 16.9328 19.876L8.15189 19.8811C7.64227 19.8814 7.2143 19.5 7.16174 18.9931C6.84059 15.8953 6.25 9.5 6.25 9.5" stroke="#FF0000" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" />
      <path d="M20 6.55469H5" stroke="#FF0000" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" />
      <path d="M17 6.55288C15.9225 6.55288 15.5944 5.67353 15.542 4.75719C15.504 4.09153 15.0116 3.5 14.3448 3.5H10.6552C9.98842 3.5 9.49604 4.09153 9.45799 4.75719C9.40562 5.67353 9.07753 6.55288 8 6.55288" stroke="#FF0000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const MobileCloseCreateHoldingEntity = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="28" rx="14" fill="#E8E9ED" />
      <path fillRule="evenodd" clipRule="evenodd" d="M18.4194 19.7297L8.51991 9.83019L9.58057 8.76953L19.4801 18.669L18.4194 19.7297Z" fill="#585D66" />
      <path fillRule="evenodd" clipRule="evenodd" d="M19.4801 9.83109L9.58056 19.7306L8.5199 18.6699L18.4194 8.77043L19.4801 9.83109Z" fill="#585D66" />
    </svg>
  )
}

const HoldingEntitySvg = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M13.25 0.25H0.34375V16.4375C0.34375 17.2832 1.02931 17.9688 1.875 17.9688H8.21875V16.6563H1.875C1.75419 16.6563 1.65625 16.5583 1.65625 16.4375V1.5625H13.25C13.3708 1.5625 13.4688 1.66044 13.4688 1.78125V7.57812H14.7812V1.78125C14.7812 0.935564 14.0957 0.25 13.25 0.25ZM10.1371 9.75679C10.1928 9.44765 9.98727 9.15192 9.67813 9.09626C9.36899 9.04059 9.07326 9.24608 9.01759 9.55522C8.96231 9.86224 8.856 10.1632 8.71247 10.4545C8.3978 10.3254 8.07545 10.3223 7.86032 10.3223C6.85282 10.3223 5.87455 10.682 5.07121 11.2456C4.4176 11.7041 3.6904 12.4909 3.7131 13.5577C3.71969 13.8671 3.81333 14.213 4.13644 14.4264C4.40229 14.6019 4.71557 14.6031 4.89686 14.599C5.61904 14.5826 6.26298 14.3216 6.82154 14.0124C7.59922 13.582 8.27396 12.9633 8.82687 12.3108L8.85687 12.2752C8.89985 12.3312 8.94531 12.3874 8.99352 12.4427C9.25658 12.7443 9.65932 13.0514 10.1368 13.1429C10.8157 13.2731 11.5578 13.3341 12.3196 13.0575C12.5834 12.9617 12.8637 12.8231 13.1042 12.7043C13.2119 12.6511 13.3116 12.6018 13.3983 12.5621C13.4321 12.5466 13.454 12.5368 13.4713 12.5298C13.545 12.5577 13.6351 12.6139 13.7655 12.7172C13.796 12.7413 13.8329 12.7718 13.8741 12.8058C13.9906 12.902 14.1417 13.0268 14.2809 13.1183C14.7659 13.4371 15.4162 13.6792 16.1247 13.4313C16.3476 13.3534 16.5551 13.2535 16.7332 13.1669L16.7653 13.1512C16.9382 13.0671 17.0837 12.9963 17.2321 12.9395L17.2321 12.9395C17.2323 12.9395 17.2344 12.9387 17.239 12.9373C17.244 12.9358 17.2509 12.9339 17.2607 12.9314C17.2768 12.9274 17.2904 12.9244 17.3099 12.9201L17.3463 12.9119L17.3466 12.9118C17.4123 12.897 17.541 12.8678 17.6695 12.8007C17.948 12.6554 18.0559 12.3118 17.9105 12.0334C17.7665 11.7574 17.4278 11.6489 17.1507 11.7885L17.1496 11.7888C17.1444 11.7903 17.1286 11.7949 17.095 11.8025L17.0764 11.8067L17.0763 11.8067C17.0517 11.8122 17.0167 11.82 16.9858 11.8277C16.9419 11.8386 16.8859 11.8541 16.8258 11.8771C16.6286 11.9525 16.4399 12.0444 16.2769 12.1239L16.2768 12.1239L16.2357 12.1439C16.0546 12.232 15.9018 12.3042 15.7491 12.3577C15.5119 12.4406 15.2416 12.3886 14.9057 12.1677C14.8198 12.1113 14.7496 12.0531 14.6577 11.9769C14.6051 11.9333 14.5454 11.8838 14.4717 11.8254C14.3 11.6894 14.0638 11.5184 13.7682 11.4302C13.3857 11.3161 13.0977 11.4484 12.9353 11.523L12.9243 11.528C12.7693 11.5991 12.6377 11.6646 12.5161 11.7252L12.516 11.7252C12.3142 11.8257 12.1398 11.9126 11.9313 11.9883C11.4539 12.1616 10.9526 12.1411 10.3509 12.0258C10.2037 11.9975 10.0101 11.8777 9.85069 11.6949C9.76373 11.5952 9.6821 11.4807 9.58988 11.344L9.5541 11.2906C9.82373 10.8253 10.0373 10.3108 10.1371 9.75679ZM7.86032 11.4598C7.93403 11.4598 7.99676 11.4613 8.05097 11.4647C8.02073 11.5019 7.99007 11.5388 7.95904 11.5754C7.46639 12.1568 6.89421 12.6721 6.27069 13.0172C5.7902 13.2832 5.33352 13.4513 4.87105 13.4617L4.85125 13.4621C4.87337 12.9858 5.21596 12.5335 5.72451 12.1767C6.36643 11.7264 7.12074 11.4598 7.86032 11.4598Z" fill="#1A1B1D" />
    </svg>
  )
}